import { useState } from "react";
import type { ReactNode } from "react";
import { avatarColorClass, initials } from "./format";
import { useAvatarUrl } from "./avatars";

/**
 * One avatar for the whole app: shows the person's photo when they have
 * one, their coloured initials when they don't.
 *
 * Deliberately keeps the existing `.av` / `.dav` wrapper and swaps only
 * its CONTENTS, rather than rendering a bare <img>. Those classes already
 * carry every call site's sizing, and several nest a presence dot inside
 * the circle — replacing the wrapper outright would have meant redoing
 * sizing and re-homing those dots at ~20 call sites.
 */
export default function Avatar({
  uid,
  name,
  size = "av",
  className = "",
  colored = true,
  style,
  children,
}: {
  uid?: string;
  name?: string;
  /** Wrapper class carrying the circle's size. `av` is the larger one
   * (message rows, rail), `dav` the compact one; call overlays pass their
   * own (they're portaled outside the workspace and styled separately). */
  size?: string;
  className?: string;
  /** Off for avatars with their own fixed fill, e.g. your own orange one. */
  colored?: boolean;
  style?: React.CSSProperties;
  /** Presence dots and similar overlays that live inside the circle. */
  children?: ReactNode;
}) {
  const url = useAvatarUrl(uid);
  const color = colored ? avatarColorClass(uid || name) : "";
  // A cached URL that no longer actually loads (an expired or deleted
  // photo) used to show nothing at all — the <img> failed silently and
  // there was no path back to the initials fallback. Track load failure
  // per-URL so a genuinely new upload gets its own fresh chance to load.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showPhoto = url && url !== failedUrl;

  return (
    <span className={`${size} ${color} ${className}`.trim()} style={style}>
      {showPhoto ? (
        <img className="avatar-img" src={url} alt="" onError={() => setFailedUrl(url)} />
      ) : (
        initials(name)
      )}
      {children}
    </span>
  );
}
